import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { fetchJson } from "./client";
import type {
  AnalysisResponse,
  EntryDetail,
  FormOptionsResponse,
  HistoryCursor,
  HistoryEntryType,
  HistoryResponse,
  OdometerListResponse,
  OverviewResponse,
  RangeKey,
} from "./types";

/** Stable query keys, so invalidation from mutations is unambiguous. */
export const queryKeys = {
  overview: (limit: number) => ["overview", { limit }] as const,
  history: (limit: number, types: readonly HistoryEntryType[]) =>
    ["history", { limit, types }] as const,
  odometer: (carId: number | null | undefined, limit: number) =>
    ["odometer", { carId: carId ?? null, limit }] as const,
  formOptions: () => ["form-options"] as const,
  analysis: (range: RangeKey, start?: string, end?: string) =>
    ["analysis", { range, start, end }] as const,
  entry: (id: number | null) => ["entry", id] as const,
};

export function useOverview(historyLimit = 30) {
  return useQuery({
    queryKey: queryKeys.overview(historyLimit),
    queryFn: () =>
      fetchJson<OverviewResponse>(`/api/overview?history_limit=${historyLimit}`),
  });
}

export function useHistoryEntries(
  limit = 10,
  types: readonly HistoryEntryType[] = ["fuel", "odometer"],
) {
  return useInfiniteQuery({
    queryKey: queryKeys.history(limit, types),
    queryFn: ({ pageParam }: { pageParam: HistoryCursor | null }) => {
      const params = new URLSearchParams({ limit: String(limit) });
      params.set("types", types.join(","));
      if (pageParam) {
        params.set("cursor_datetime", pageParam.datetime);
        params.set("cursor_id", String(pageParam.id));
        params.set("cursor_type", pageParam.entry_type);
      }
      return fetchJson<HistoryResponse>(`/api/history?${params.toString()}`);
    },
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
  });
}

export function useOdometerEntries(carId: number | null | undefined, limit = 1000) {
  return useQuery({
    queryKey: queryKeys.odometer(carId, limit),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(limit) });
      if (carId) params.set("car_id", String(carId));
      return fetchJson<OdometerListResponse>(`/api/odometer?${params.toString()}`);
    },
    enabled: carId != null,
  });
}

export function useFormOptions() {
  return useQuery({
    queryKey: queryKeys.formOptions(),
    queryFn: () => fetchJson<FormOptionsResponse>("/api/form-options"),
    // Static-ish data — fine to keep cached for the session.
    staleTime: 1000 * 60 * 60,
  });
}

export function useEntry(id: number | null) {
  return useQuery({
    queryKey: queryKeys.entry(id),
    queryFn: () => fetchJson<EntryDetail>(`/api/entries/${id}`),
    enabled: id !== null,
  });
}

export function useAnalysis(range: RangeKey, start?: string, end?: string) {
  const params = new URLSearchParams({ range });
  if (range === "custom" && start && end) {
    params.set("start", start);
    params.set("end", end);
  }
  return useQuery({
    queryKey: queryKeys.analysis(range, start, end),
    queryFn: () =>
      fetchJson<AnalysisResponse>(`/api/analysis?${params.toString()}`),
    // The custom range needs both dates; otherwise we'd hit a 400.
    enabled: range !== "custom" || (Boolean(start) && Boolean(end)),
  });
}
